import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Button } from '@/components/ui/button';

interface Props {
  children: ReactNode;
  fallbackMessage?: string;
}

interface State {
  hasError: boolean;
}

export class FeatureErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  /**
   * The exception goes to the console; the page keeps showing its one sentence.
   *
   * `getDerivedStateFromError` alone made every render-time throw behind this
   * boundary invisible outside the one user's screen — "Something went wrong" with no
   * stack anywhere, so nothing was diagnosable without a user reproducing it (spec 136
   * R-F36). `RootErrorComponent` reports the same way for the same reason; the
   * component stack is included because the fallback cannot say which subtree failed.
   */
  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error(
      `Render failed behind a feature error boundary: ${this.props.fallbackMessage ?? 'Something went wrong'}`,
      error,
      info.componentStack,
    );
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col items-center justify-center gap-4 p-8 text-sm">
          <p className="text-destructive">
            {this.props.fallbackMessage ?? 'Something went wrong'}
          </p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => this.setState({ hasError: false })}
          >
            Try again
          </Button>
        </div>
      );
    }
    return this.props.children;
  }
}
